import { usePermission } from '@backstage/plugin-permission-react';
import { useRouteRef } from '@backstage/frontend-plugin-api';
import { Flex, Grid, Skeleton, Text } from '@backstage/ui';
import { pulseTrackReadPermission } from '@internal/backstage-plugin-pulse-common';
import { ComplianceGauge } from './compliance-gauge';
import { InsightsCardShell } from './insights-card-shell';
import { usePulseTeamInsights } from './use-pulse-team-insights';
import { insightsRouteRef } from '../../routes';

const GENERIC_ERROR_MESSAGE =
  'Could not load your Pulse compliance right now. Please try again later.';

const GAUGE_SIZE = 72;

const WRAP_STYLE = { flexWrap: 'wrap' } as const;
const GAUGE_GRID_STYLE = { flex: '1 1 410px', minWidth: 0 } as const;
const METRIC_GRID_STYLE = { flex: '1 1 210px', minWidth: 0 } as const;

const buildInsightsHref = (
  insightsPath: string | undefined,
  selectedOwnerRefs: string[],
) => {
  if (!insightsPath || selectedOwnerRefs.length === 0) {
    return insightsPath;
  }
  const query = new URLSearchParams({ owners: selectedOwnerRefs.join(',') });
  return `${insightsPath}?${query.toString()}`;
};

type PulseMetricProps = {
  label: string;
  value: string | number;
};

const PulseMetric = ({ label, value }: PulseMetricProps) => (
  <Flex direction="column" gap="1">
    <Text variant="title-small" weight="bold">
      {value}
    </Text>
    <Text variant="body-small" color="secondary">
      {label}
    </Text>
  </Flex>
);

const PulseInsightsCardContent = () => {
  const getInsightsPath = useRouteRef(insightsRouteRef);
  const { value, loading, error } = usePulseTeamInsights();

  if (loading) {
    return (
      <InsightsCardShell>
        <Skeleton width="100%" height={36} />
        <Text variant="body-small" color="secondary">
          Loading your team's compliance...
        </Text>
      </InsightsCardShell>
    );
  }

  if (error) {
    return (
      <InsightsCardShell>
        <Text variant="body-small">{GENERIC_ERROR_MESSAGE}</Text>
      </InsightsCardShell>
    );
  }

  if (!value?.insights || value.insights.summary.entitiesInScope === 0) {
    return null;
  }

  const {
    entitiesInScope,
    tracksInScope,
    mandatoryCompliance,
    averageCompliance,
  } = value.insights.summary;

  return (
    <InsightsCardShell
      teamLabel={value.teamLabels.join(', ')}
      insightsHref={buildInsightsHref(
        getInsightsPath?.(),
        value.selectedOwnerRefs,
      )}
    >
      <Flex
        direction="row"
        align="center"
        justify="between"
        gap="6"
        px="1"
        py="3"
        style={WRAP_STYLE}
      >
        <Grid.Root columns={{ initial: '1', sm: '2' }} style={GAUGE_GRID_STYLE}>
          <Grid.Item>
            <ComplianceGauge
              percentage={mandatoryCompliance}
              label="Mandatory Checks"
              size={GAUGE_SIZE}
            />
          </Grid.Item>
          <Grid.Item>
            <ComplianceGauge
              percentage={averageCompliance}
              label="All checks"
              size={GAUGE_SIZE}
              fillOnComplete
            />
          </Grid.Item>
        </Grid.Root>
        <Grid.Root columns={{ initial: '2' }} gap="6" style={METRIC_GRID_STYLE}>
          <Grid.Item>
            <PulseMetric label="Entities in scope" value={entitiesInScope} />
          </Grid.Item>
          <Grid.Item>
            <PulseMetric label="Tracks in scope" value={tracksInScope} />
          </Grid.Item>
        </Grid.Root>
      </Flex>
    </InsightsCardShell>
  );
};

export const PulseInsightsCard = () => {
  const { allowed: canReadTracks } = usePermission({
    permission: pulseTrackReadPermission,
  });

  // Hooks run even when a component renders null, so the gate gates the mount.
  return canReadTracks ? <PulseInsightsCardContent /> : null;
};
